import { useCallback, FC, useMemo, useRef } from "react";
import { ItemListItemKind, ItemListItemRowData } from "@/components/item-list/types";
import { ReactMouseEvent } from "@/domains/react/types";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { MDSRowItem, MDSRowItemSlotName, MDSTextColor, MDSTextWeight, MDSSpacer } from "@/mds";
import { MDSText, MDSTextVariant } from "@/mds";
import { MDSPanelWidths } from "@/mds/foundations";
import {
  ItemListRowDropdown,
  ItemListTitleIcon,
  ItemListItemSharedByBox,
  ItemListToggleIcon,
} from "@/components/item-list/rows/components";

export interface ItemListItemRowProps {
  data: ItemListItemRowData;
  noSeparator?: boolean;
}

export const ItemListItemRow: FC<ItemListItemRowProps> = observer(
  ({ data: { payload: item, mustClickOnRowTitle = false }, noSeparator }) => {
    const itemPreviewState = useMemo(() => item.createPreviewState?.(), [item]);

    const extraRowContent = useMemo(
      () => item.extraRow && { id: item.extraRow.id, content: item.extraRow.content?.() },
      [item]
    );

    const handleMouseEnter = item.listState?.isHoverable
      ? () => item.listState?.highlightItem(item.id)
      : undefined;

    const isHighlighted = item.id === item.listState?.highlightedItemId;
    const isSelected = item.listState?.isSelected(item.id);

    const itemChips = item.chips?.() || null;
    const hasChips = !!itemChips;

    const itemRef = useRef(item);
    itemRef.current = item;

    const handleItemClick = useCallback((event: ReactMouseEvent, canClickItem = true) => {
      event.stopPropagation();
      const item = itemRef.current;
      if (item.listState?.canSelect && event.shiftKey) {
        item.listState.selectItemsFromLastSelectionUntil(item.id);
        return;
      }

      // if (item.listState?.isSelectModeActive || (isMac() ? event.metaKey : event.ctrlKey)) {
      //   item.listState?.toggleItemSelection(item.id);
      //   return;
      // }

      if (canClickItem) {
        item.onClick({ itemId: item.id, event });
      }
    }, []);

    const handleRowClick = (event: ReactMouseEvent) => handleItemClick(event, !mustClickOnRowTitle);

    const handleTitleClick = (event: ReactMouseEvent) => handleItemClick(event);

    const rightAlignedComponent = item.rightAlignedComponent?.(isHighlighted);
    const rightComponentTriggersActions = !!rightAlignedComponent;
    const showActions =
      (!item.listState?.isSelectModeActive || !isSelected) &&
      (rightComponentTriggersActions || (isHighlighted && !!item.dropdown));
    const showChipsAndShare = !rightAlignedComponent || !showActions;

    const preview =
      itemPreviewState?.previewUi && itemPreviewState?.anchorPreviewAtTheEnd ? (
        <div slot={MDSRowItemSlotName.LastAddon}>{itemPreviewState.previewUi}</div>
      ) : (
        itemPreviewState?.previewUi
      );

    const canSelect = item.listState?.canSelect ?? false;

    const isSearchOrMessage = [ItemListItemKind.Search, ItemListItemKind.Message].includes(
      item.kind
    );

    return (
      <MDSRowItem
        $isSelected={isSelected}
        $isHighlighted={isHighlighted}
        $isSpecial={item.isSpecial}
        $appearance={item.appearance}
        $mustClickOnRowTitle={mustClickOnRowTitle}
        noSeparator={noSeparator}
        className="mds-item-list-row" // Used in e2e tests
        onClick={handleRowClick}
        onMouseEnter={handleMouseEnter}
      >
        <ItemListToggleIcon
          slot={MDSRowItemSlotName.LeftAddon}
          item={item}
          isSelected={isSelected}
          isHighlighted={canSelect && isHighlighted}
          itemRef={itemRef}
          handleItemClick={handleItemClick}
        />
        <ContentRow>
          <Content>
            <LabelContainer
              onClick={handleTitleClick}
              onMouseEnter={itemPreviewState?.handleMouseEnter}
              onMouseLeave={itemPreviewState?.handleMouseLeave}
            >
              {isSearchOrMessage && (
                <MDSText
                  color={MDSTextColor.Emphasis}
                  variant={MDSTextVariant.TextS}
                  weight={MDSTextWeight.Smedium}
                >
                  {item.label}
                </MDSText>
              )}
              {!isSearchOrMessage && (
                <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.Medium}>
                  {item.label}
                </MDSText>
              )}
              {showChipsAndShare && !!item.titleIcon && (
                <ItemListTitleIcon titleIcon={item.titleIcon} />
              )}
              {showChipsAndShare && (
                <ItemChipsContainerExpanded>{itemChips}</ItemChipsContainerExpanded>
              )}
            </LabelContainer>
          </Content>

          {!showActions && (
            <SharedByAndDateContainer
              $expandWidth={showChipsAndShare && !!item.sharedBy && !!item.dateLabel}
            >
              {showChipsAndShare && !!item.sharedBy && (
                <ItemListItemSharedByBox
                  sharedBy={item.sharedBy}
                  sharedByVariant={item.sharedByVariant}
                />
              )}
              {!!item.dateLabel && (
                <StyledText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
                  {item.dateLabel}
                </StyledText>
              )}
            </SharedByAndDateContainer>
          )}
        </ContentRow>

        {extraRowContent && (
          <BottomRow
            slot={MDSRowItemSlotName.BottomAddon}
            key={extraRowContent.id}
            onMouseEnter={itemPreviewState?.handleMouseEnter}
            onMouseLeave={itemPreviewState?.handleMouseLeave}
          >
            {!!hasChips && <ItemChipsContainerCollapsed>{itemChips}</ItemChipsContainerCollapsed>}
            <BottomRowContainer>{extraRowContent.content}</BottomRowContainer>
          </BottomRow>
        )}

        {preview}

        {showActions && (
          <RightSide
            $expandWidth={!!rightAlignedComponent && !!item.dropdown}
            slot={MDSRowItemSlotName.RightAddon}
          >
            {rightAlignedComponent}
            {item.dropdown && (
              <>
                <MDSSpacer />
                <ItemListRowDropdown itemId={item.id} dropdown={item.dropdown} />
              </>
            )}
          </RightSide>
        )}
      </MDSRowItem>
    );
  }
);

const ContentRow = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  flex-shrink: 1;
`;

const BottomRow = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text.secondary};
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const Content = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
`;

const LabelContainer = styled.div`
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const RightSide = styled.div<{ $expandWidth?: boolean }>`
  align-items: flex-end;
  display: flex;
  margin-left: 10px;
  width: ${({ $expandWidth }) => ($expandWidth ? "auto" : "60px")};
  min-width: 60px;
`;

const StyledText = styled(MDSText)`
  justify-self: flex-end;
  text-align: right;
  line-height: 20px;
  flex-shrink: 1;
`;

const SharedByAndDateContainer = styled.div<{ $expandWidth?: boolean }>`
  display: flex;
  flex-direction: row;

  align-items: flex-end;
  gap: 16px;

  min-width: 0;
  margin-left: 10px;

  justify-content: ${({ $expandWidth }) => ($expandWidth ? "space-between" : "flex-end")};
  flex-shrink: ${({ $expandWidth }) => ($expandWidth ? 1 : 0)};
  width: ${({ $expandWidth }) => ($expandWidth ? "200px" : "auto")};

  @container mds-row-item (max-width: ${MDSPanelWidths.headerBreakpoint}px) {
    width: min-content;
  }
`;

const ItemChipsContainerExpanded = styled.div`
  @container mds-row-item (max-width: ${MDSPanelWidths.narrow}px) {
    display: none;
  }
`;

const BottomRowContainer = styled.div``;

const ItemChipsContainerCollapsed = styled.div`
  @container mds-row-item (min-width: ${MDSPanelWidths.narrow}px) {
    display: none;
  }
`;
